import { Section } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import TableRow from './table/row.gts';
import { deleteExistingRecord, saveRecord } from 'tio-data/requests/save-record';
import type { CommentsMap } from '../tuition-assistance/approver-comments.ts';
import type { CommentExtension as CommentModel } from 'tio-data/data/resources/comment.ext';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { Store } from '@warp-drive/core';

interface ApproverCommentsTableSignature {
  Args: {
    currentPerson: PersonModel;
    model: CommentsMap;
    onClose: () => void;
  };
}

export default class ApproverCommentsTableComponent extends Component<ApproverCommentsTableSignature> {
  @service declare store: Store;

  showExpandableCommentSection = (key: string) => {
    if (key === 'programInstance' || key === 'application') {
      return false;
    }
    return true;
  };

  save = async (comment: CommentModel) => {
    if (comment.isNew) {
      this.args.onClose();
    }
    // WarpDrive mutation builder (Phase 2.7) — replaces comment.save()
    await saveRecord(this.store, comment);
  };

  rollback = async (comment: CommentModel) => {
    if (comment.isNew) {
      this.args.onClose();
      // WarpDrive mutation builder (Phase 2.7) — replaces comment.destroyRecord()
      await deleteExistingRecord(this.store, comment);
      return;
    }
    comment.rollbackAttributes();
  };

  delete = async (comment: CommentModel) => {
    // WarpDrive mutation builder (Phase 2.7) — replaces comment.destroyRecord()
    await deleteExistingRecord(this.store, comment);
  };

  <template>
    <table class="table-fixed w-full">
      <tbody>
        {{#each-in @model as |key comments|}}
          {{#if (this.showExpandableCommentSection key)}}
            <Section @collapsed={{true}}>
              <:header>
                <p class="text-wrap text-left">
                  {{t "common.tuition_assistance.comments_for_application_id"}}
                  <span class="font-semibold">{{key}}</span>
                </p>
              </:header>
              <:body>
                {{#each comments as |comment|}}
                  <TableRow
                    @model={{comment}}
                    @canEdit={{eq @currentPerson.id comment.person.id}}
                    @canDelete={{@currentPerson.user.isTioAdmin}}
                    @onSave={{this.save}}
                    @onCancel={{this.rollback}}
                    @onDelete={{this.delete}}
                  />
                {{/each}}
              </:body>
            </Section>
          {{else}}
            {{#each comments as |comment|}}
              <TableRow
                @model={{comment}}
                @canEdit={{eq @currentPerson.id comment.person.id}}
                @canDelete={{@currentPerson.user.isTioAdmin}}
                @onSave={{this.save}}
                @onCancel={{this.rollback}}
                @onDelete={{this.delete}}
              />
            {{/each}}
          {{/if}}
        {{else}}
          <tr class="tio-bg-surface h-9">
            <td colspan="3" class="px-2 tio-fg-muted text-center">
              {{t "common.tuition_assistance.no_comments_found"}}
            </td>
          </tr>
        {{/each-in}}
      </tbody>
    </table>
  </template>
}
